// src/utils/AuthContext.js
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  accessControlAPI,
  authAPI,
  clearAccessToken,
  setAccessToken,
  setSessionExpiredHandler,
} from '../services/api';

const AuthContext = createContext();

const EMPTY_ACCESS = {
  groups: [],
  permissions: [],
  policy: {},
};

function isPlatformAdminUser(user) {
  return (
    user?.role === 'PLATFORM_ADMIN' ||
    user?.is_superuser === true ||
    user?.is_staff === true
  );
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [access, setAccess] = useState(EMPTY_ACCESS);
  const [loading, setLoading] = useState(true);

  const clearAuth = useCallback(() => {
    clearAccessToken();
    setUser(null);
    setAccess(EMPTY_ACCESS);
  }, []);

  const loadAuthorizedProfile = useCallback(async () => {
    const profileRes = await authAPI.getProfile();
    const profileUser = profileRes.data;

    if (isPlatformAdminUser(profileUser)) {
      setUser(profileUser);
      setAccess(EMPTY_ACCESS);
      return profileUser;
    }

    const accessRes = await accessControlAPI.getMyPermissions();
    const authorizedUser = accessRes.data?.user || profileUser;
    setAccess({
      groups: accessRes.data?.groups || [],
      permissions: accessRes.data?.permissions || [],
      policy: accessRes.data?.policy || {},
    });
    setUser(authorizedUser);
    return authorizedUser;
  }, []);

  const bootstrapAuth = useCallback(async () => {
    setLoading(true);
    try {
      await authAPI.bootstrapSession();
      await loadAuthorizedProfile();
    } catch {
      // No usable refresh cookie or authorization was revoked. This is a normal
      // unauthenticated page-load state and must not create a redirect loop.
      clearAuth();
    } finally {
      setLoading(false);
    }
  }, [clearAuth, loadAuthorizedProfile]);

  useEffect(() => {
    bootstrapAuth();
  }, [bootstrapAuth]);

  useEffect(() => {
    return setSessionExpiredHandler(() => {
      clearAuth();
      if (window.location.pathname !== '/login') {
        window.location.assign('/login?session=expired');
      }
    });
  }, [clearAuth]);

  const login = useCallback(async (userData, tokens) => {
    if (!tokens?.access) {
      throw new Error('Authentication response did not include an access token.');
    }

    setLoading(true);
    setUser(null);
    setAccess(EMPTY_ACCESS);
    setAccessToken(tokens.access);

    try {
      return await loadAuthorizedProfile();
    } catch (error) {
      clearAuth();
      throw error;
    } finally {
      setLoading(false);
    }
  }, [clearAuth, loadAuthorizedProfile]);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.warn('Logout API failed; local session state will still be cleared.', error);
    } finally {
      clearAuth();
      window.location.assign('/login');
    }
  }, [clearAuth]);

  const reloadAuth = useCallback(async () => {
    setLoading(true);
    try {
      return await loadAuthorizedProfile();
    } catch (error) {
      clearAuth();
      throw error;
    } finally {
      setLoading(false);
    }
  }, [clearAuth, loadAuthorizedProfile]);

  const hasPermission = useCallback(
    (permissionCode) => {
      if (!permissionCode) return true;
      return access.permissions.includes(permissionCode);
    },
    [access.permissions],
  );

  const hasAnyPermission = useCallback(
    (permissionCodes = []) => {
      if (!permissionCodes.length) return true;
      return permissionCodes.some((code) => access.permissions.includes(code));
    },
    [access.permissions],
  );

  const hasAllPermissions = useCallback(
    (permissionCodes = []) => {
      if (!permissionCodes.length) return true;
      return permissionCodes.every((code) => access.permissions.includes(code));
    },
    [access.permissions],
  );

  const isPlatformAdmin = useMemo(
    () => isPlatformAdminUser(user),
    [user],
  );

  const isSuperAdmin = useMemo(
    () =>
      isPlatformAdmin ||
      access.groups.some((group) => group.name === 'Super Admin'),
    [isPlatformAdmin, access.groups],
  );

  const isPrivacyOperationsAdmin = useMemo(
    () => access.groups.some((group) => group.name === 'Privacy Operations Admin'),
    [access.groups],
  );

  const value = {
    user,
    access,
    permissions: access.permissions,
    groups: access.groups,
    policy: access.policy,

    loading,
    isAuthenticated: !loading && !!user,

    login,
    logout,
    reloadAuth,

    hasPermission,
    hasAnyPermission,
    hasAllPermissions,

    isPlatformAdmin,
    isSuperAdmin,
    isPrivacyOperationsAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
